import {
  CalendarMonth,
  DragIndicator,
  FolderOutlined,
  QuizOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ScreenMobile } from "../../../../components/common/ScreenMobile";
import { useSessionContext } from "../../../../contexts/SessionContext";
import { BundlePatientSummary } from "../../../../services/fhir";
import { HTTPResponseStatus } from "../../../../utils/enum";
import { useFhirData } from "../../../../utils/useFhirData";
import { MyfamilyForDash } from "../../../myfamily/MyfamilyForDash";
import { PatientDocs } from "../../../ehrrecords/components/patientSummaryAccordions/Documents/PatientDocs";
import { ScheduleForDash } from "../ScheduleForDash";
import { PatientQuestionnaireTasksSection } from "./components/PatientQuestionnaireTasksSection";
import SectionHeader from "src-next/shared/ui/sections/SectionHeader";
import { ControlMedications } from "./compontents/ControlMedications";
import { FitnessDataModal } from "./compontents/FitnessDataModal";
import { MoreOptionsWeb } from "./compontents/MoreOptionsWeb";
import { ViewDocs } from "./compontents/ViewDocs";
import { PatientDashMobile } from "./components/PatientDashMobile";

type MobileActionId =
  | "notes"
  | "exames"
  | "procedures"
  | "certificates"
  | "family"
  | "medications";

const DOCS_TITLE: Partial<Record<string, string>> = {
  Notes: "Receitas médicas",
  Exames: "Solicitações de exame",
  Procedures: "Procedimentos",
  Certificates: "Relatórios e atestados",
};

// ─── Componente principal ─────────────────────────────────────────────────────

export function PatientDash() {
  const { user } = useSessionContext();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Modais — usados no mobile (web: MoreOptionsWeb gerencia os próprios)
  const [docsType, setDocsType] = useState<string>("");
  const [openFamily, setOpenFamily] = useState(false);
  const [openMedications, setOpenMedications] = useState(false);
  const [openFitness, setOpenFitness] = useState(false);
  const [openAgendaFull, setOpenAgendaFull] = useState(false);

  // Bundle clínico — para PatientDocs no desktop
  const [summaryData, setSummaryData] = useState<any>();
  const [refetch, setRefetch] = useState(false);
  /** Ordem das seções arrastáveis no dashboard web */
  const [sectionOrder, setSectionOrder] = useState([
    "questionnaires",
    "schedule-docs",
  ]);
  /** Contagem de questionários abertos — chip no SectionHeader ao lado do título */
  const [openQuestionnaireCount, setOpenQuestionnaireCount] = useState(0);

  // Sensor do DnD — ativa drag após mover 8px (evita conflito com cliques)
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const medicalNoteData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "prescricao",
  )?.[0]?.entry;
  const solicitationData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "service-request",
  )?.[0]?.entry;
  const NoteReportData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "atestado",
  )?.[0]?.entry;
  const resultsData = useFhirData.extractPatientSummaryResource(
    summaryData,
    "DocumentReference",
    "resultado",
  );

  useEffect(() => {
    async function loadBundle() {
      try {
        const res = await BundlePatientSummary({
          identifier: user?.username,
          purposeofuse: "Atendimento",
        });
        if (res?.status === HTTPResponseStatus.Forbidden) navigate("/");
        setSummaryData(res);
      } catch (err) {
        console.log("PatientDash loadBundle error:", err);
      }
    }
    if (user?.username) loadBundle();
  }, [refetch, navigate, user]);

  // Reabre fitness após retorno do OAuth (desktop: modal; mobile: seção já embutida)
  useEffect(() => {
    const shouldReopen =
      localStorage.getItem("reopenFitnessModalAfterLink") === "true";
    if (!shouldReopen || !user?.refreshTokenGoogleFit) return;
    localStorage.removeItem("reopenFitnessModalAfterLink");
    if (!isMobile) setOpenFitness(true);
  }, [user?.refreshTokenGoogleFit, isMobile]);

  const handleMobileAction = (id: MobileActionId) => {
    switch (id) {
      case "notes":
        setDocsType("Notes");
        break;
      case "exames":
        setDocsType("Exames");
        break;
      case "procedures":
        setDocsType("Procedures");
        break;
      case "certificates":
        setDocsType("Certificates");
        break;
      case "family":
        setOpenFamily(true);
        break;
      case "medications":
        setOpenMedications(true);
        break;
    }
  };

  return (
    <Stack spacing={0} sx={{ pb: isMobile ? 1 : 3, width: "100%" }}>
      {isMobile ? (
        <PatientDashMobile
          userName={user?.name}
          refreshTokenGoogleFit={user?.refreshTokenGoogleFit || ""}
          openAgendaFull={openAgendaFull}
          onOpenAgendaFull={setOpenAgendaFull}
          onMobileAction={handleMobileAction}
        />
      ) : (
        /* ─── WEB (desktop) ───────────────────────────────────────── */
        <Stack spacing={2} sx={{ px: 1, pt: 1 }}>
          <MoreOptionsWeb onOpenFitnessData={() => setOpenFitness(true)} />

          {/* Seções arrastáveis */}
          <DndContext
            sensors={dndSensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (over && active.id !== over.id) {
                setSectionOrder((prev) => {
                  const oldIdx = prev.indexOf(String(active.id));
                  const newIdx = prev.indexOf(String(over.id));
                  return arrayMove(prev, oldIdx, newIdx);
                });
              }
            }}
          >
            <SortableContext
              items={sectionOrder}
              strategy={verticalListSortingStrategy}
            >
              <Stack spacing={2}>
                {sectionOrder.map((id) =>
                  id === "questionnaires" ? (
                    <SortableSection
                      key={id}
                      id={id}
                      title="Questionários"
                      icon={<QuizOutlined fontSize="small" />}
                      badge={
                        openQuestionnaireCount > 0 ? (
                          <Chip
                            label={`${openQuestionnaireCount} pendente${openQuestionnaireCount > 1 ? "s" : ""}`}
                            size="small"
                            color="warning"
                            sx={{
                              fontWeight: 700,
                              height: 22,
                              fontSize: "0.7rem",
                            }}
                          />
                        ) : undefined
                      }
                    >
                      <PatientQuestionnaireTasksSection
                        variant="tutor"
                        onOpenTasksCountChange={setOpenQuestionnaireCount}
                      />
                    </SortableSection>
                  ) : (
                    <SortableSection
                      key={id}
                      id={id}
                      title="Agenda & Documentos"
                      icon={<CalendarMonth fontSize="small" />}
                    >
                      <ResizablePanels
                        left={
                          <>
                            <SubSectionLabel
                              icon={<CalendarMonth fontSize="small" />}
                              label="Agenda"
                            />
                            <ScheduleForDash />
                          </>
                        }
                        right={
                          <>
                            <SubSectionLabel
                              icon={<FolderOutlined fontSize="small" />}
                              label="Documentos"
                            />
                            <PatientDocs
                              setRefetch={setRefetch}
                              sign
                              medicalNoteData={medicalNoteData}
                              solicitationData={solicitationData}
                              resultsData={resultsData}
                              NoteReportData={NoteReportData}
                              summaryData={summaryData}
                            />
                          </>
                        }
                      />
                    </SortableSection>
                  ),
                )}
              </Stack>
            </SortableContext>
          </DndContext>
        </Stack>
      )}

      {/* ─── Modais (mobile e fitness compartilhado) ──────────────── */}

      {docsType && (
        <ScreenMobile
          open={!!docsType}
          onClose={() => setDocsType("")}
          title={DOCS_TITLE[docsType] ?? "Documentos"}
        >
          <ViewDocs type={docsType} />
        </ScreenMobile>
      )}

      {openFamily && (
        <ScreenMobile
          open={openFamily}
          onClose={() => setOpenFamily(false)}
          title="Minha família"
        >
          <MyfamilyForDash />
        </ScreenMobile>
      )}

      {openMedications && isMobile ? (
        <ScreenMobile
          open={openMedications}
          onClose={() => setOpenMedications(false)}
          title="Meus Medicamentos"
          subtitle="Gerencie seus medicamentos"
        >
          <ControlMedications
            embedded
            open={openMedications}
            setOpen={setOpenMedications}
          />
        </ScreenMobile>
      ) : openMedications ? (
        <ControlMedications
          open={openMedications}
          setOpen={setOpenMedications}
        />
      ) : null}

      {!isMobile && (
        <FitnessDataModal
          open={openFitness}
          onClose={() => setOpenFitness(false)}
          refreshToken={user?.refreshTokenGoogleFit || ""}
        />
      )}
    </Stack>
  );
}

// ─── ResizablePanels ─────────────────────────────────────────────────────────

const AGENDA_MIN = 280;
const AGENDA_MAX = 720;
const AGENDA_DEFAULT = 480;

/**
 * Dois painéis lado a lado com divisor arrastável entre eles.
 * O painel esquerdo tem largura controlada; o direito ocupa o restante.
 */
function ResizablePanels({
  left,
  right,
}: {
  left: React.ReactNode;
  right: React.ReactNode;
}) {
  const [leftWidth, setLeftWidth] = useState(AGENDA_DEFAULT);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(AGENDA_DEFAULT);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      /* Impede que o DndKit interprete o arrasto do resizer como drag de seção */
      e.stopPropagation();
      dragging.current = true;
      startX.current = e.clientX;
      startWidth.current = leftWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [leftWidth],
  );

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging.current) return;
    const delta = e.clientX - startX.current;
    const newWidth = Math.min(
      AGENDA_MAX,
      Math.max(AGENDA_MIN, startWidth.current + delta),
    );
    setLeftWidth(newWidth);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        minWidth: 0,
        width: "100%",
      }}
    >
      {/* Painel esquerdo — largura controlada pelo resize */}
      <Box sx={{ width: leftWidth, flexShrink: 0, minWidth: 0 }}>{left}</Box>

      {/* ── Handle de redimensionamento ── */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          width: 8,
          flexShrink: 0,
          alignSelf: "stretch",
          cursor: "col-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: 0.5,
          borderRadius: 1,
          transition: "background-color 0.15s ease",
          "&:hover": { bgcolor: "action.hover" },
          "&:active": { bgcolor: "action.selected" },
        }}
      >
        {/* Linha central visual do handle */}
        <Box
          sx={{
            width: 2,
            height: "60%",
            minHeight: 32,
            borderRadius: 99,
            bgcolor: "divider",
            transition: "background-color 0.15s ease",
            ".MuiBox-root:hover > &": { bgcolor: "primary.light" },
          }}
        />
      </Box>

      {/* Painel direito — ocupa o restante */}
      <Box sx={{ flex: 1, minWidth: 0 }}>{right}</Box>
    </Box>
  );
}

// ─── SubSectionLabel ─────────────────────────────────────────────────────────

/**
 * Rótulo leve para separar sub-seções dentro de um SortableSection.
 * Não colapsa — é apenas um indicador visual de contexto.
 */
function SubSectionLabel({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mb: 1.5,
        color: "text.secondary",
      }}
    >
      <Box sx={{ display: "flex", color: "primary.main", opacity: 0.7 }}>
        {icon}
      </Box>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          fontSize: "0.7rem",
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ─── SortableSection ──────────────────────────────────────────────────────────

interface SortableSectionProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  /** Chip/conteúdo ao lado do título (ex.: pendentes) — opcional */
  badge?: React.ReactNode;
}

/**
 * Wrapper arrastável com SectionHeader.
 * O ícone DragIndicator no actions do SectionHeader é o handle de drag.
 * O conteúdo colapsa via isCollapsed do SectionHeader.
 */
function SortableSection({
  id,
  title,
  icon,
  children,
  badge,
}: SortableSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 999 : "auto",
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: isDragging ? "primary.main" : "divider",
          bgcolor: "neutral0.main",
          overflow: "visible",
          transition: "border-color 0.15s ease",
        }}
      >
        <CardContent
          sx={{ py: 2, px: { xs: 2, md: 3 }, "&:last-child": { pb: 2 } }}
        >
          <SectionHeader
            icon={icon}
            title={title}
            badge={badge}
            isCollapsed={isCollapsed}
            onCollapsedChange={setIsCollapsed}
            actions={
              /* Handle de drag — arrastar aqui para reordenar */
              <Tooltip title="Arrastar para reordenar" arrow>
                <IconButton
                  size="small"
                  {...attributes}
                  {...listeners}
                  sx={{
                    cursor: isDragging ? "grabbing" : "grab",
                    color: "text.disabled",
                    "&:hover": { color: "text.secondary" },
                    touchAction: "none",
                  }}
                >
                  <DragIndicator fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          >
            {children}
          </SectionHeader>
        </CardContent>
      </Card>
    </Box>
  );
}
